import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useRules } from "hooks/useRules";
import { TrendingUp, CheckCircle2, AlertCircle, Download } from "lucide-react";
import Skeleton from "components/Skeleton";
import PageKpis from "components/PageKpis";
import { makeSearchHelpers } from "utilities/searchDynamic";
import { useSearch } from "context/SearchContext";
import { useAuth } from "utilities/auth";
import { isInternal } from "utilities/userType";

const rulesHelpers = makeSearchHelpers("rules");

export default function Rules() {
  const navigate = useNavigate();
  const { filters } = useSearch();
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("dateUpdated");
  const [sortDir, setSortDir] = useState("desc");
  const { user } = useAuth();

  const isSoundAdmin =
    isInternal(user) && user?.companyId === "sound-admin";

  const params = useMemo(() => {
    return rulesHelpers.toApiParams(filters, { pruneDefaults: true });
  }, [filters]);

  const {
    rules,
    meta,
    loading,
    loadingMore,
    error,
    fetchRules,
    loadMore,
    hasMore,
  } = useRules({ params });

  // Load rules when filters change
  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  // --- Filtering
  const filteredRules = useMemo(() => {
    const searchLower = searchTerm.toLowerCase();
    const list = Array.isArray(rules) ? rules : [];

    return list.filter(
      (rule) =>
        !searchTerm ||
        rule.name?.toLowerCase().includes(searchLower) ||
        rule.scope?.value?.toLowerCase().includes(searchLower) ||
        rule.constraints?.some(
          (c) =>
            c.type === "CAMPAIGN" &&
            c.value?.toLowerCase().includes(searchLower)
        )
    );
  }, [rules, searchTerm]);

  // --- Sorting
  const sorted = useMemo(() => {
    const rows = [...filteredRules];
    const dir = sortDir === "asc" ? 1 : -1;

    rows.sort((a, b) => {
      const va = accessor(a, sortBy);
      const vb = accessor(b, sortBy);

      if (typeof va === "string" || typeof vb === "string") {
        return String(va).localeCompare(String(vb)) * dir;
      }
      return (Number(va) - Number(vb)) * dir;
    });
    return rows;
  }, [filteredRules, sortBy, sortDir]);

  const data = sorted;

  // --- CSV download
  function downloadCSV() {
    if (!data?.length) return;

    const headers = [
      "Rule Name",
      "Company",
      "BIN",
      "Campaign",
      "MID(s)",
      "Approval Before",
      "Approval After",
      "Approval Delta",
    ];

    const rows = data.map((r) => [
      r.name || "",
      isSoundAdmin ? r.companyId || "" : "",
      r.scope?.value || "",
      r.constraints?.find((c) => c.type === "CAMPAIGN")?.value || "ALL",
      Array.isArray(r.routing)
        ? r.routing.length === 1
          ? r.routing[0].mid
          : `Multiple (${r.routing.length})`
        : "",
      r.performance?.approvalBefore != null
        ? (r.performance.approvalBefore * 100).toFixed(1) + "%"
        : "",
      r.performance?.approvalAfter != null
        ? (r.performance.approvalAfter * 100).toFixed(1) + "%"
        : "",
      r.performance?.approvalDelta != null
        ? (r.performance.approvalDelta * 100).toFixed(1) + "%"
        : "",
    ]);

    const csvContent = [headers, ...rows]
      .map((r) => r.map((v) => `"${v}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "rules_export.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // --- KPI calculations (now using meta)
  const totalRevenue = rules?.reduce(
    (sum, r) => sum + (r.performance?.forecastedRevenue || 0),
    0
  );
  const totalActualRevenue = rules?.reduce(
    (sum, r) => sum + (r.performance?.actualRevenue || 0),
    0
  );

  if (error) return <p className="text-destructive">Error: {error.message}</p>;

  return (
    <div className="space-y-6">
      {/* KPIs */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="surface rounded-xl ring-1 ring-border p-5">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <Skeleton.Rect className="h-3 w-24 mt-3" />
                </div>
                <div className="grid place-items-center rounded-lg">
                  <Skeleton.Rect className="h-5 w-5 mt-3" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <PageKpis
          items={[
            {
              key: "forecasted",
              label: "Total Projected Revenue",
              value: totalRevenue,
              format: "currency",
              currency: "USD",
              tone: "success",
              icon: <TrendingUp />,
            },
            {
              key: "actual",
              label: "Total Actual Revenue",
              value: totalActualRevenue,
              format: "currency",
              currency: "USD",
              tone: "info",
              icon: <TrendingUp />,
            },
            {
              key: "live",
              label: "Live Rules",
              value: meta.live,
              tone: "success",
              icon: <CheckCircle2 />,
            },
            {
              key: "inactive",
              label: "Inactive Rules",
              value: meta.inactive,
              tone: "destructive",
              icon: <AlertCircle />,
            },
          ]}
        />
      )}

      {/* Search + Meta */}
      <div className="flex justify-between items-center">
        <input
          type="text"
          placeholder="Search rules..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="input input-bordered w-64"
        />

        <div className="flex items-center space-x-4">
          <p className="text-sm text-muted-foreground">
            Showing {rules.length} of {meta.total} rules
          </p>

          <button
            onClick={downloadCSV}
            className="btn btn-ghost inline-flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            Download CSV
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="surface rounded-xl ring-1 ring-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-muted">
              <tr>
                <Th
                  label="Name"
                  field="name"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={setSortBy}
                />
                <Th
                  label="Date"
                  field="dateUpdated"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={setSortBy}
                />
                <Th
                  label="BIN"
                  field="scope.value"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={setSortBy}
                />
                <Th
                  label="Brand"
                  field="brand"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={setSortBy}
                />
                <Th
                  label="Campaign"
                  field="campaign"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={setSortBy}
                />
                <Th
                  label="Country"
                  field="country"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={setSortBy}
                />
                <Th
                  label="MID"
                  field="merchantId"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={setSortBy}
                />
                <Th
                  label="% Before"
                  field="approvalRateBefore"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={setSortBy}
                />
                <Th
                  label="% After"
                  field="approvalRateAfter"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={setSortBy}
                />
                <Th
                  label="Delta"
                  field="approvalRateChange"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={setSortBy}
                />
              </tr>
            </thead>

            <tbody className="bg-card divide-y divide-border">
              {data.map((r) => (
                <tr
                  key={`${r.ruleId}-${r.version}`}
                  onClick={() => navigate(`/rules/${r.ruleId}`)}
                  className="hover:bg-muted transition-colors cursor-pointer"
                >
                  <td className="px-6 py-4 text-xs">{r.name}</td>
                  <td className="px-6 py-4 text-xs">
                    {r.updatedAt
                      ? new Date(r.updatedAt).toLocaleDateString()
                      : "—"}
                  </td>
                  <td className="px-6 py-4 text-xs">{r.scope?.value}</td>
                  <td className="px-6 py-4 text-xs">
                    {r.constraints?.find((c) => c.type === "CATEGORY")?.value ||
                      "—"}
                  </td>
                  <td className="px-6 py-4 text-xs">
                    {r.constraints?.find((c) => c.type === "CAMPAIGN")?.value ||
                      "ALL"}
                  </td>
                  <td className="px-6 py-4 text-xs">
                    {r.constraints?.find((c) => c.type === "COUNTRY")?.value ||
                      "GLOBAL"}
                  </td>
                  <td className="px-6 py-4 text-xs font-mono">
                    {Array.isArray(r.routing) && r.routing.length > 0
                      ? r.routing.length === 1
                        ? r.routing[0].mid
                        : `Multiple (${r.routing.length})`
                      : "—"}
                  </td>
                  <td className="px-6 py-4 text-xs">
                    {r.performance?.approvalBefore != null
                      ? `${(r.performance.approvalBefore * 100).toFixed(1)}%`
                      : "—"}
                  </td>
                  <td className="px-6 py-4 text-xs">
                    {r.performance?.approvalAfter != null
                      ? `${(r.performance.approvalAfter * 100).toFixed(1)}%`
                      : "—"}
                  </td>
                  <td className="px-6 py-4 text-xs">
                    {r.performance?.approvalDelta != null
                      ? `${(r.performance.approvalDelta * 100).toFixed(1)}%`
                      : "—"}
                  </td>
                </tr>
              ))}

              {!loading && data.length === 0 && (
                <tr>
                  <td
                    colSpan={11}
                    className="px-6 py-12 text-center text-muted-foreground"
                  >
                    No rules match your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Load more */}
          {hasMore && (
            <div className="p-4 flex justify-center bg-card border-t border-border">
              <button
                onClick={loadMore}
                disabled={loadingMore}
                className="btn btn-secondary"
              >
                {loadingMore ? "Loading..." : "Load More"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function accessor(row, field) {
  const perf = row.performance || {};
  switch (field) {
    case "name":
      return row.name || "";
    case "dateUpdated":
      return row.updatedAt ? new Date(row.updatedAt).getTime() : 0;
    case "scope.value":
      return row.scope?.value || "";
    case "brand":
      return row.constraints?.find((c) => c.type === "CATEGORY")?.value || "";
    case "campaign":
      return row.constraints?.find((c) => c.type === "CAMPAIGN")?.value || "";
    case "country":
      return row.constraints?.find((c) => c.type === "COUNTRY")?.value || "";
    case "merchantId":
      return row.routing?.[0]?.mid || "";
    case "approvalRateBefore":
      return perf.approvalBefore ?? 0;
    case "approvalRateAfter":
      return perf.approvalAfter ?? 0;
    case "approvalRateChange":
      return perf.approvalDelta ?? 0;
    default:
      return 0;
  }
}

function Th({ label, field, sortBy, sortDir, onSort }) {
  const active = sortBy === field;
  const dir = active ? sortDir : undefined;
  return (
    <th
      scope="col"
      className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer select-none"
      onClick={() => onSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <span
          className={`text-xs ${
            active ? "text-foreground" : "text-muted-foreground"
          }`}
        >
          {active ? (dir === "asc" ? "▲" : "▼") : "↕"}
        </span>
      </span>
    </th>
  );
}
